import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Music, BarChart3, User, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItem = "text-[13px] 2xl:text-sm tracking-wide text-neutral-300 hover:text-[#D4AF37] transition-colors";
  const active = ({ isActive }) => `${navItem} ${isActive ? "text-[#D4AF37]" : ""}`;

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 xl:px-6 2xl:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img
            src="/hoh-vibes-logo-128.png"
            alt="HOH_Vibes"
            width={44}
            height={44}
            className="w-11 h-11 rounded-full object-cover ring-1 ring-[#D4AF37]/40"
          />
          <div className="leading-tight whitespace-nowrap">
            <div className="font-serif-display text-xl text-white">HOH<span className="text-[#D4AF37]">_</span>Vibes</div>
            <div className="text-[10px] tracking-[0.25em] text-neutral-500 uppercase xl:hidden 2xl:block">Music · Faith · Data</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6 whitespace-nowrap" data-testid="nav-desktop">
          <NavLink to="/music" className={active} data-testid="nav-music">Music</NavLink>
          <NavLink to="/templates" className={active} data-testid="nav-templates">Templates</NavLink>
          <NavLink to="/ebooks" className={active} data-testid="nav-ebooks">E-books</NavLink>
          <NavLink to="/merch" className={active} data-testid="nav-merch">Merch</NavLink>
          <NavLink to="/church-builder" className={active} data-testid="nav-church-builder">Church Builder</NavLink>
          <NavLink to="/attendance" className={active} data-testid="nav-attendance">Attendance</NavLink>
          <NavLink to="/projection" className={active} data-testid="nav-projection">Worship Projection</NavLink>
          <NavLink to="/app" className={active} data-testid="nav-app">App</NavLink>
          <NavLink to="/about" className={active} data-testid="nav-about">About</NavLink>
          <NavLink to="/contact" className={active} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="nav-basket" aria-label="View basket" title="View Basket" className="relative p-2 text-neutral-300 hover:text-[#D4AF37] transition-colors">
            <ShoppingBag size={20} />
            {items.length > 0 && (
              <span data-testid="basket-count" className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {items.length}
              </span>
            )}
          </Link>
          {user ? (
            <div className="hidden xl:flex items-center gap-3">
              {user.role === "admin" ? (
                <Link to="/admin" data-testid="nav-admin" className={navItem}>Admin</Link>
              ) : (
                <>
                  <Link to="/dashboard" data-testid="nav-dashboard" className={navItem}>Dashboard</Link>
                  <Link to="/downloads" data-testid="nav-downloads" className={navItem}>Downloads</Link>
                </>
              )}
              <button data-testid="nav-logout" onClick={() => { logout(); navigate("/"); }} className="text-neutral-300 hover:text-[#D4AF37]">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden xl:inline-flex whitespace-nowrap btn-ghost-gold px-4 py-2 text-xs tracking-widest uppercase">
              Sign In
            </Link>
          )}
          <button onClick={() => setOpen(!open)} className="xl:hidden text-white" data-testid="nav-menu-toggle">
            <Menu size={22} />
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-white/10 bg-black/90 px-6 py-4 space-y-3 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <Link onClick={() => setOpen(false)} to="/music" className="block text-neutral-200">Music</Link>
          <Link onClick={() => setOpen(false)} to="/templates" className="block text-neutral-200">Templates</Link>
          <Link onClick={() => setOpen(false)} to="/ebooks" className="block text-neutral-200">E-books</Link>
          <Link onClick={() => setOpen(false)} to="/merch" className="block text-neutral-200">Merch</Link>
          <Link onClick={() => setOpen(false)} to="/church-builder" className="block text-neutral-200">Church Builder</Link>
          <Link onClick={() => setOpen(false)} to="/attendance" data-testid="nav-mobile-attendance" className="block text-neutral-200">Attendance</Link>
          <Link onClick={() => setOpen(false)} to="/projection" data-testid="nav-mobile-projection" className="block text-neutral-200">Worship Projection</Link>
          <Link onClick={() => setOpen(false)} to="/app" className="block text-neutral-200">App</Link>
          <Link onClick={() => setOpen(false)} to="/about" className="block text-neutral-200">About</Link>
          <Link onClick={() => setOpen(false)} to="/contact" className="block text-neutral-200">Contact</Link>
          {user ? (
            <>
              {user.role === "admin"
                ? <Link onClick={() => setOpen(false)} to="/admin" className="block text-neutral-200">Admin</Link>
                : <>
                    <Link onClick={() => setOpen(false)} to="/dashboard" className="block text-neutral-200">Dashboard</Link>
                    <Link onClick={() => setOpen(false)} to="/downloads" className="block text-neutral-200">Downloads</Link>
                  </>}
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-neutral-200">Sign Out</button>
            </>
          ) : (
            <Link onClick={() => setOpen(false)} to="/login" className="block text-[#D4AF37]">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
